import { RefObject, useEffect, useRef } from 'react';

import useUpdateViewerPageState from '../useUpdateViewerPageState';

const COMMIT_DEBOUNCE_MS = 200;

interface Props {
  rootRef: RefObject<HTMLElement>;
  /** 프로그래매틱 스크롤 진행 중에는 옵저버 커밋을 억제한다 */
  isProgrammaticScrollRef: RefObject<boolean>;
  totalPages: number;
}

/**
 * 연속 스크롤 모드에서 "현재 페이지"를 감지해 currentPageAtom을 갱신한다.
 * rootMargin으로 뷰포트를 세로 중앙선으로 수축시켜, 중앙선을 지나는 페이지
 * 정확히 1개만 intersecting 상태가 되도록 한다. 200ms 디바운스로 빠른
 * 플링 시 스쳐 지나간 페이지가 체류시간에 기록되는 것을 방지한다.
 */
const useScrollCurrentPage = ({
  rootRef,
  isProgrammaticScrollRef,
  totalPages,
}: Props) => {
  const { updateCurrentPage } = useUpdateViewerPageState();
  const updateCurrentPageRef = useRef(updateCurrentPage);
  updateCurrentPageRef.current = updateCurrentPage;

  const observerRef = useRef<IntersectionObserver | null>(null);
  const commitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastCommittedPageRef = useRef<number | null>(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root || totalPages === 0) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const intersecting = entries.find((entry) => entry.isIntersecting);
        if (!intersecting) {
          return;
        }

        const page = Number((intersecting.target as HTMLElement).dataset.page);
        if (!page || page === lastCommittedPageRef.current) {
          return;
        }

        if (commitTimeoutRef.current) {
          clearTimeout(commitTimeoutRef.current);
        }

        commitTimeoutRef.current = setTimeout(() => {
          if (isProgrammaticScrollRef.current) {
            return;
          }
          lastCommittedPageRef.current = page;
          updateCurrentPageRef.current(page);
        }, COMMIT_DEBOUNCE_MS);
      },
      {
        root,
        /* 뷰포트를 세로 중앙선으로 수축 → 중앙선을 지나는 페이지 1개만 교차 */
        rootMargin: '-50% 0px -50% 0px',
        threshold: 0,
      },
    );

    observerRef.current = observer;

    const pageElements = root.querySelectorAll('[data-page]');
    pageElements.forEach((element) => observer.observe(element));

    return () => {
      if (commitTimeoutRef.current) {
        clearTimeout(commitTimeoutRef.current);
      }
      observer.disconnect();
      observerRef.current = null;
    };
  }, [rootRef, isProgrammaticScrollRef, totalPages]);

  /** 프로그래매틱 이동 후 커밋 기준점을 갱신해 유령 커밋을 방지한다 */
  const syncCommittedPage = (page: number) => {
    lastCommittedPageRef.current = page;
  };

  return { syncCommittedPage };
};

export default useScrollCurrentPage;
